import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/lib/i18n';
import { errMsg } from '@/lib/api';
import { PillotLogo } from '@/components/ModuleIcon';
import { LanguageToggle } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';

export const AuthLayout = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen bg-auth-wash relative noise">
    <div className="absolute top-4 left-4 z-10"><Link to="/" className="text-xs font-medium text-muted-foreground hover:text-primary" data-testid="auth-back-home">← pillot.id</Link></div>
    <div className="absolute top-4 right-4 z-10"><LanguageToggle /></div>
    <div className="mx-auto flex min-h-screen max-w-6xl items-center px-4 py-10">
      <div className="grid w-full gap-10 lg:grid-cols-2 items-center">
        <div className="hidden lg:block fade-up">
          <PillotLogo className="h-14" withTagline />
          <h2 className="mt-8 font-display text-4xl font-semibold leading-tight tracking-tight text-foreground">
            ERP modular untuk bisnis yang ingin tumbuh.
          </h2>
          <p className="mt-4 text-base text-muted-foreground max-w-md">
            Pilih aplikasi yang Anda butuhkan: Website Builder dengan AI, Pengaturan, dan modul lain yang segera hadir. Bayar per pengguna, tanpa kontrak.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {['Website dua bahasa (ID/EN) dengan domain sendiri', 'Google Analytics & Meta Pixel siap pakai', 'Asisten AI untuk menulis konten website', 'Langganan per pengguna, pembayaran via Xendit'].map((f) => (
              <li key={f} className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-[hsl(var(--pillot-mint))]" />{f}</li>
            ))}
          </ul>
        </div>
        <div className="w-full max-w-md mx-auto rounded-2xl border border-border bg-white p-8 shadow-soft fade-up">
          <div className="lg:hidden mb-6"><PillotLogo className="h-8" /></div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          <div className="mt-6">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
        </div>
      </div>
    </div>
  </div>
);

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(form.email.trim(), form.password);
      navigate(params.get('next') || (data.companies?.length ? '/apps' : data.user?.is_super_admin ? '/admin' : '/onboarding'));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t('login_title')} subtitle={t('login_sub')} footer={<>{t('no_account')} <Link to="/register" className="font-medium text-primary hover:underline" data-testid="login-register-link">{t('register')}</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">{t('email')}</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" className="rounded-xl h-11" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between"><Label htmlFor="password">{t('password')}</Label><Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="login-forgot-link">Lupa kata sandi?</Link></div>
          <PasswordInput id="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="login-password-input" className="rounded-xl h-11" />
        </div>
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-xl" data-testid="login-form-submit-button">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <>{t('login')} <ArrowRight size={16} className="ml-1" /></>}
        </Button>
      </form>
    </AuthLayout>
  );
}
